import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/DialogSheet';
import { REVIEW_URL } from '@/config';
import { useSubscribed, useUser } from '@/hooks/user';
import ImageRateUs from '@/icons/rate-us.svg';
import ThumbsUp from '@/icons/thumbs-up.svg';
import { setIsModalRateUsOpen } from '@/store/myDocuments';
import { updateUser } from '@/store/profile/thunks';
import { hasPassedDays, showError } from '@/utils';

function RateUs() {
	const user = useUser();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const isEmpty = useSelector(state => state.myDocuments.isEmpty);
	const isLessThanSevenDays = !hasPassedDays(user.createdAt, 7);
	const isSubscribed = useSubscribed();
	const open = useSelector(state => state.myDocuments.isModalRateUsOpen);

	const handleClick = useCallback(
		async function () {
			setLoading(true);
			dispatch(updateUser({ reviewRequested: true }))
				.unwrap()
				.then(() => {
					setLoading(false);
				})
				.catch(error => {
					showError(error);
					setLoading(false);
				});
		},
		[dispatch]
	);

	const handleClose = () => {
		dispatch(setIsModalRateUsOpen(false));
		dispatch(updateUser({ reviewRequested: true }));
	};

	const isVisible =
		!isEmpty && !user.reviewRequested && isSubscribed && isLessThanSevenDays;

	if (!isVisible) {
		return null;
	}

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				className="w-full sm:max-w-[397px]"
				onOpenAutoFocus={e => e.preventDefault()}
				onInteractOutside={ev => ev.preventDefault()}
			>
				<DialogTop title={t('myDocuments.rateUs.title')} />

				<div className="p-4">
					<div className="flex flex-col items-start justify-between gap-4 rounded-lg bg-light-red px-4 pt-6 sm:flex-row sm:items-end sm:pt-0">
						<div className="flex flex-col items-start gap-2 sm:gap-4 sm:py-6">
							<ThumbsUp
								aria-hidden
								className="h-6 w-6 text-red md:h-8 md:w-8"
							/>
							<p className="mb-1 text-xs font-semibold leading-5">
								{t('myDocuments.rateUs.contentTitle')}
							</p>
							<p className="text-xs leading-5">
								{t('myDocuments.rateUs.contentDescription')}
							</p>
						</div>

						<div>
							<ImageRateUs title={t('myDocuments.rateUs.title')} />
						</div>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={handleClose}>
						{t('common.cancel')}
					</Button>
					<Button
						Component="a"
						target="_blank"
						href={REVIEW_URL}
						onClick={handleClick}
						disabled={loading}
					>
						{t('myDocuments.rateUs.button')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export default RateUs;
